/** @format */
import React, { useContext, useEffect } from "react"
import {
    ContentPage,
    Pages,
    isContentActive,
} from "../../../components/contentful"
import { PageTypes } from "../../../components/contentful/page"
import {
    getI18n,
    getPageContents,
} from "../../../components/contentful/pageProps"
import { Context } from "../../../context"
import { slugify } from "../../../util"
import { API } from "../../../util/api"
import generateStoreSchema from "../../../util/generateStoreSchema"
import { handleError } from "../../../util/handlers"
import { NextUtils } from "../../../util/next"

const Locations = ({
    city,
    content,
    state,
    store,
    stores = [],
    type,
    storeClosedBanner,
    ...props
}) => {
    const { preview } = props
    const { dispatch } = useContext(Context)

    useEffect(() => {
        dispatch({
            type: "STORES",
            payload: stores,
        })
    }, [dispatch, stores])

    if (!city) return null

    let breadcrumbs = []
    switch (type) {
        case PageTypes.City: {
            const storeCity = city
            const stateAbbreviation = state.stateAbbreviation

            breadcrumbs = [
                {
                    href: "/locations",
                    description: "Locations",
                },
                {
                    href: `/locations/${slugify(
                        state.stateAbbreviation.toLowerCase()
                    )}`,
                    description: state.stateName,
                },
                {
                    href: `/locations/${slugify(
                        state.stateAbbreviation.toLowerCase()
                    )}/${slugify(storeCity)}`,
                    description: storeCity,
                },
            ]

            return (
                <ContentPage
                    {...{
                        content,
                        context: {
                            dmaName: null,
                            msoName: null,
                            stateAbbreviation,
                            stateName: state.stateName,
                            cityName: storeCity,
                        },
                        breadcrumbs,
                        preview,
                        type: Pages[type],
                    }}
                />
            )
        }
        case PageTypes.Store:
        case PageTypes.StoreOffers: {
            dispatch({ type: `CURRENT_STORE`, payload: store })

            const cityPath = `/locations/${slugify(
                state.stateAbbreviation.toLowerCase()
            )}/${`${slugify(`${store?.storeCity} ${store?.storeId}`)}`}`

            breadcrumbs = [
                {
                    href: "/locations",
                    description: "Locations",
                },
                {
                    href: `/locations/${slugify(
                        state.stateAbbreviation.toLowerCase()
                    )}`,
                    description: state.stateName,
                },
                {
                    href: `/locations/${slugify(
                        state.stateAbbreviation.toLowerCase()
                    )}/${slugify(store?.storeCity)}`,
                    description: store?.storeCity,
                },
                {
                    href: cityPath,
                    description: store?.storeAddress,
                },
            ]

            const newContent = {
                city,
                dmaName: null,
                msoName: null,
                stateAbbreviation: state.stateAbbreviation.toLowerCase(),
                stateName: store?.storeState,
                cityName: store?.storeCity,
                streetAddress1: store?.storeAddress,
                storeCity: slugify(store?.storeCity),
                postCode: store?.storePostcode,
                storeId: store?.storeId,
                localContent: store?.localContent,
            }

            switch (type) {
                case PageTypes.Store: {
                    // filter out inactive promos
                    let { promos } = props
                    promos = promos.filter((promo) =>
                        isContentActive({ ...promo, preview })
                    )

                    const schema = generateStoreSchema(
                        store,
                        state.stateAbbreviation
                    )

                    return (
                        <ContentPage
                            {...{
                                city,
                                content,
                                context: newContent,
                                breadcrumbs,
                                promos,
                                store,
                                storeClosedBanner,
                                preview,
                                type: Pages[type],
                                schema,
                            }}
                        />
                    )
                }
                case PageTypes.StoreOffers:
                    breadcrumbs.push({
                        href: `${cityPath}/offers`,
                        description: "Offers",
                    })

                    return (
                        <ContentPage
                            {...{
                                city,
                                content,
                                context: newContent,
                                breadcrumbs,
                                store,
                                preview,
                                type: Pages[type],
                            }}
                        />
                    )
            }

            break
        }
        default:
            return null
    }
}

export default Locations

export async function getStaticPaths({ defaultLocale }) {
    let paths = []

    try {
        const cityAndStorePaths = await NextUtils.getCityAndStorePaths({
            defaultLocale,
        })

        paths = cityAndStorePaths
    } catch (err) {
        console.log("Static Path Error [city]")
        handleError(err)
    }

    return {
        paths, // comment out if we want to render the Store pages on demand
        // paths: cityPaths, // add back if rendering store pages on demand
        // paths: [], // don't pre-render path; all pages should be rendered on-demand
        fallback: "blocking", // render the page on first request
    }
}

export async function getStaticProps({ defaultLocale, params, ...props }) {
    let { city, state } = params
    const [slug, ...route] = city
    city = slug.toLowerCase()

    const i18next = await getI18n({ locale: defaultLocale })
    const states = i18next.t("states")

    const stateMatch = states?.find(
        (item) => item.stateAbbreviation.toUpperCase() === state.toUpperCase()
    )

    let stores = null
    let type = null

    // If there isn't a state confirmed/ matched, this is likely a DMA page caught by the catch all
    // This is due to additional, incorrect parameters (ex. /locations/charlotte-area/tel:{phone}).
    if (!stateMatch?.stateName && state?.includes("area")) {
        return {
            redirect: {
                destination: `/locations/${state}/`,
                permanent: false,
                // statusCode: 301
            },
        }
    }

    // If state is valid, proceed
    if (stateMatch && stateMatch.stateName) {
        if (
            city.indexOf(`-`) === -1 ||
            isNaN(city.substring(city.lastIndexOf(`-`) + 1, city.length))
        ) {
            city = city
                .split(`-`)
                .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
                .join(" ")

            const request = {
                city,
                state: stateMatch.stateName,
            }

            stores = await API.getLocations(request)

            if (!stores || !stores?.length) {
                // If there is a server error,
                // throw an error instead of returning so that the cache is not updated
                // until the next successful request.
                throw new Error(`Failed to fetch stores for city page`)
            }

            type = PageTypes.City

            const pageType = Pages[type]

            return await getPageContents(
                {
                    city,
                    state: stateMatch,
                    stores,
                    type,
                    notFound: !city || !stores?.length,
                    ...props,
                },
                pageType
            )
        } else {
            type = PageTypes.Store
            if (route?.length) type = PageTypes.StoreOffers
            const storeProps = await NextUtils.getStoreProps({
                params: { ...params, state: stateMatch },
                type,
                ...props,
            })

            if (!storeProps) {
                handleError("Store data missing for provided id", false, [])
                return {
                    redirect: {
                        destination: `/locations/${state}/`,
                        permanent: false,
                    },
                }
            }

            return storeProps
        }
    } else {
        // Default to city page if state is not valid and return a not found page
        type = PageTypes.City

        const pageType = Pages[type]

        return await getPageContents(
            {
                city,
                state,
                stores,
                type,
                notFound: true,
                ...props,
            },
            pageType
        )
    }
}
